import { PollAnswer, SurveyPlatform } from "../types/common";
import { LmsPollAnswer } from "../types/smartLms";
import { VbPollAnswer, Question } from "../types/valuebuilder";

/**
 * Validates a radio group question
 */
const validateRadioGroup = (question: Question): boolean => {
  return !!(
    question.is_required &&
    question.is_displayed &&
    (!question.value ||
      (question.value &&
        question.questionsGroup &&
        Object.keys(question.value).length !==
          Object.keys(question.questionsGroup).length))
  );
};

/**
 * Validates a standard question
 * @returns true if there's a validation error
 */
const validateStandardQuestion = (question: Question): boolean => {
  return !!(question.is_required && question.is_displayed && !question.value);
};

/**
 * Creates a poll answer from a question
 * @returns PollAnswer object
 */
const createPollAnswer = (
  question: Question,
  value: string | Record<string, string>,
  platform: SurveyPlatform
): Array<PollAnswer> => {
  if (platform === "Lms") {
    if (typeof value === "object") {
      return Object.keys(value).map((key) => ({
        questionId: key,
        answer: value[key],
      }));
    }
    return [{ [question.id]: value }];
  }

  return [
    {
      value: typeof value === "object" ? JSON.stringify(value) : value,
      surveyPollQuestion: `/survey_poll_questions/${question.question_id}`,
    },
  ];
};

/**
 * Validates all questions, builds poll answers, and sets error states in one pass
 */
export const validateAndBuildAnswersWithErrors = (
  questions: Question[],
  platform: SurveyPlatform = "Vb"
): {
  hasErrors: boolean;
  answers: Array<PollAnswer>;
  updatedQuestions: Question[];
  firstErrorIndex: number;
} => {
  let hasErrors = false;
  let firstErrorIndex = 0;
  let answers: Array<PollAnswer> = [];

  const updatedQuestions = questions.map((question, index) => {
    const questionCopy = { ...question };

    if (question.type === "radiogroup") {
      if (validateRadioGroup(question)) {
        hasErrors = true;
        firstErrorIndex = firstErrorIndex || index + 1;
        questionCopy.error = true;
      } else if (question.value) {
        answers = [
          ...answers,
          ...createPollAnswer(question, question.value, platform),
        ];
        questionCopy.error = false;
      }
    } else {
      // Handle standard question
      if (validateStandardQuestion(question)) {
        hasErrors = true;
        firstErrorIndex = firstErrorIndex || index + 1;
        questionCopy.error = true;
      } else if (question.value) {
        answers = [
          ...answers,
          ...createPollAnswer(question, question.value, platform),
        ];
        questionCopy.error = false;
      }

      // Handle child questions
      if (question.children?.length) {
        questionCopy.children = question.children.map((child) => {
          const childCopy = { ...child };
          if (validateStandardQuestion(child)) {
            hasErrors = true;
            firstErrorIndex = firstErrorIndex || index + 1;
            childCopy.error = true;
          } else if (child.value) {
            answers = [
              ...answers,
              ...createPollAnswer(child, child.value, platform),
            ];
            childCopy.error = false;
          }
          return childCopy;
        });
      }
    }

    return questionCopy;
  });

  return { hasErrors, answers, updatedQuestions, firstErrorIndex };
};
